import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Drawer, IconButton } from "@mui/material";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SelectGroup from "../../../components/FormComponent/SelectGroup";
import InputGroup from "../../../components/FormComponent/InputGroup";
import TextAreaGroup from "../../../components/FormComponent/TextAreaGroup";
import Button from "../../../components/Button";
import { httprequest } from "../../../data/api";
import useStyles from "../styles";

const DESTINATIONS = [
  { value: "assignment", label: "Assignment" },
  { value: "test", label: "Test" },
  { value: "school_exam", label: "School Exam" },
  { value: "enrolment_exam", label: "Enrolment Exam" }
];

const DIFFICULTY_OPTIONS = ["Easy", "Medium", "Hard"];

const SAVE_LABELS = {
  assignment: "Save to Assignment",
  test: "Save to Test",
  school_exam: "Save to School Exam",
  enrolment_exam: "Save to Enrolment Exam"
};

const isDraftOrEmpty = (status) => {
  const normalized = String(status ?? "").trim().toLowerCase();

  return normalized === "draft" || normalized === "empty" || normalized === "";
};

const getDestinationLabel = (item) => {
  if (item?.title) return item.title;
  if (item?.subject?.name) return item.subject.name;
  if (item?.name) return item.name;

  return "Untitled";
};

const CONFIG_TYPE_MAP = {
  assignment: "ASSIGNMENT",
  test: "TEST",
  school_exam: "SCHOOL_EXAM",
  enrolment_exam: "ENROLMENT_EXAM"
};

const getConfigType = (destinationType) => CONFIG_TYPE_MAP[destinationType] || destinationType;

const getDestinationDetailPath = (destinationType, configId) => {
  if (!configId) return null;

  if (destinationType === "assignment") return `/assignments/${configId}`;
  if (destinationType === "test") return `/assessment-detail/${configId}`;
  if (destinationType === "school_exam") return `/school-examination/examination-details/${configId}`;
  if (destinationType === "enrolment_exam") return `/enrolments/enrolment-examination-details/${configId}`;

  return null;
};

const normalizeQuestion = (q, index = 0) => ({
  localId: `${Date.now()}-${index}-${Math.random().toString(36).slice(2, 8)}`,
  type: q?.type || "multiple_choice",
  question_text: q?.question_text || "",
  options: Array.isArray(q?.options) ? [...q.options] : [],
  answer: Array.isArray(q?.answer) ? [...q.answer] : [],
  grade_point: Number(q?.grade_point) || 1
});

const normalizeQuestions = (questions = []) => {
  return questions.map((q, index) => normalizeQuestion(q, index));
};

const GenerateQuestionsDrawer = ({
  open,
  onClose,
  topic,
  isAdmin,
  lessonOutlineId,
  versionId,
  initialDestinationType = "",
  initialDestinationId = "",
  examContext = null
}) => {
  const isExamContext = Boolean(examContext);
  const classes = useStyles();
  const navigate = useNavigate();
  const [step, setStep] = useState("setup");
  const [difficulty, setDifficulty] = useState("Easy");
  const [questionCount, setQuestionCount] = useState(10);
  const [destinationType, setDestinationType] = useState("");
  const [destinationId, setDestinationId] = useState("");
  const [destinations, setDestinations] = useState([]);
  const [isLoadingDestinations, setIsLoadingDestinations] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [regeneratingId, setRegeneratingId] = useState(null);
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [sessionId, setSessionId] = useState("");
  const [showDestinationEditor, setShowDestinationEditor] = useState(false);
  const [description, setDescription] = useState("");

  const resetForm = () => {
    setStep("setup");
    setDifficulty("Easy");
    setQuestionCount(10);
    setDestinationType("");
    setDestinationId("");
    setDestinations([]);
    setGeneratedQuestions([]);
    setRegeneratingId(null);
    setSessionId("");
    setShowDestinationEditor(false);
    setDescription(examContext?.description || "");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const fetchDestinations = async (type, preserveSelection = false, preserveId = null) => {
    setIsLoadingDestinations(true);
    const selectedId = preserveId ?? destinationId;

    if (!preserveSelection) {
      setDestinations([]);
      setDestinationId("");
    }

    try {
      let list = [];

      if (type === "assignment") {
        const endpoint = isAdmin ? "/api/v1/assignment-configs" : "/api/v1/assignment/teacher";
        const res = await httprequest.get(endpoint);
        list = res?.data?.data || [];
      } else if (type === "test") {
        const sessionRes = await httprequest.get("/api/v1/school-plan/session");
        const sessions = sessionRes?.data?.data || [];
        const activeSession = sessions.find((session) => session?.current_session === true);
        const activeTerm = activeSession?.terms?.find((term) => term?.is_current === true);
        if (!activeTerm?.id) {
          toast.error("No active term found for tests");
          setIsLoadingDestinations(false);

          return;
        }
        const res = await httprequest.get(`/api/v1/tests?term_id=${activeTerm.id}`);
        list = res?.data?.data || [];
      } else if (type === "school_exam") {
        const endpoint = isAdmin ? "/api/v1/exams" : "/api/v1/school-exams";
        const res = await httprequest.get(endpoint);
        list = res?.data?.data || [];
      } else if (type === "enrolment_exam") {
        const res = await httprequest.get("/api/v1/enrolment-exam-configs");
        list = res?.data?.data || [];
      }

      const draftOrEmpty = (Array.isArray(list) ? list : []).filter((item) => isDraftOrEmpty(item?.status));
      setDestinations(draftOrEmpty);

      if (preserveSelection && selectedId) {
        const stillValid = draftOrEmpty.some((item) => String(item.id) === String(selectedId));
        if (!stillValid) {
          setDestinationId("");
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load destinations");
      setDestinations([]);
    } finally {
      setIsLoadingDestinations(false);
    }
  };

  const handleDestinationTypeChange = (type) => {
    setDestinationType(type);
    fetchDestinations(type);
  };

  const handleEditSetup = () => {
    const nextOpen = !showDestinationEditor;
    setShowDestinationEditor(nextOpen);

    if (nextOpen && destinationType) {
      fetchDestinations(destinationType, true);
    }
  };

  useEffect(() => {
    if (!open) {
      resetForm();

      return;
    }

    if (initialDestinationType) {
      setDestinationType(initialDestinationType);
      if (initialDestinationId) {
        setDestinationId(initialDestinationId);
      }
      if (!isExamContext) {
        fetchDestinations(initialDestinationType, Boolean(initialDestinationId), initialDestinationId);
      }
    }
    if (examContext?.description) {
      setDescription(examContext.description);
    }
  }, [open, initialDestinationType, initialDestinationId, examContext, isExamContext]);

  const handleGenerate = async () => {
    if (!isExamContext) {
      if (!lessonOutlineId || !versionId) {
        toast.error("Lesson outline details are missing");

        return;
      }
      if (!destinationType) {
        toast.error("Please select where to add the questions");

        return;
      }
      if (!destinationId) {
        toast.error("Please select a title");

        return;
      }
    } else if (!destinationType || !destinationId) {
      toast.error("Destination is missing");

      return;
    } else if (!examContext?.subjectId) {
      toast.error("Subject is missing for question generation");

      return;
    }
    if (!questionCount || Number(questionCount) < 1) {
      toast.error("Please enter a valid number of questions");

      return;
    }

    setIsGenerating(true);
    try {
      const payload = isExamContext
        ? {
          mode: "description",
          difficulty: String(difficulty).toLowerCase(),
          count: Number(questionCount),
          subject_id: examContext.subjectId,
          description: String(description || "").trim()
        }
        : {
          mode: "lesson",
          lesson_outline_id: lessonOutlineId,
          version_id: versionId,
          difficulty: String(difficulty).toLowerCase(),
          count: Number(questionCount)
        };

      const res = await httprequest.post("/api/v1/question-generation/generate", payload);
      const questions = normalizeQuestions(res?.data?.data?.questions || []);
      const generatedSessionId = res?.data?.data?.session_id || res?.data?.data?.response_id;

      if (!questions.length) {
        toast.error("No questions were generated");

        return;
      }
      if (!generatedSessionId) {
        toast.error("Generation session is missing. Please try again.");

        return;
      }

      setSessionId(generatedSessionId);
      setGeneratedQuestions(questions);
      setShowDestinationEditor(false);
      setStep("results");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to generate questions");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteQuestion = (localId) => {
    setGeneratedQuestions((prev) => prev.filter((q) => q.localId !== localId));
  };

  const handleRegenerateQuestion = async (localId) => {
    if (!sessionId) {
      toast.error("Generation session is missing");

      return;
    }

    const currentQuestion = generatedQuestions.find((q) => q.localId === localId);
    if (!currentQuestion) return;

    setRegeneratingId(localId);
    try {
      const res = await httprequest.post(`/api/v1/question-generation/${sessionId}/regenerate`, {
        question_text: currentQuestion.question_text || ""
      });
      const replacement = normalizeQuestion(res?.data?.data?.question);
      if (!replacement?.question_text) {
        toast.error("Failed to regenerate question");

        return;
      }
      setGeneratedQuestions((prev) => {
        return prev.map((q) => (q.localId === localId ? { ...replacement, localId } : q));
      });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to regenerate question");
    } finally {
      setRegeneratingId(null);
    }
  };

  const handleSave = async () => {
    if (!generatedQuestions.length) {
      toast.error("Add at least one question before saving");

      return;
    }
    if (!destinationType || !destinationId) {
      toast.error("Please select a destination");

      return;
    }

    setIsSaving(true);
    try {
      await httprequest.post("/api/v1/question-generation/batches", {
        config_type: getConfigType(destinationType),
        config_id: destinationId,
        questions: generatedQuestions.map((q) => ({
          type: q.type,
          question_text: String(q.question_text || "").trim(),
          options: q.options,
          answer: Array.isArray(q.answer) ? q.answer : [],
          grade_point: Number(q.grade_point) || 1
        }))
      });

      const detailPath = getDestinationDetailPath(destinationType, destinationId);
      toast.success("Questions saved successfully");
      handleClose();

      if (detailPath) {
        navigate(detailPath);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save questions");
    } finally {
      setIsSaving(false);
    }
  };

  const isOptionSelected = (question, optionIndex) => {
    return Array.isArray(question.answer) && question.answer.includes(optionIndex);
  };

  const renderOptionControl = (question, optionIndex) => {
    const selected = isOptionSelected(question, optionIndex);
    if (question.type === "checkbox") {
      return (
        <span className={`${classes.generateOptionControl} ${selected ? classes.generateOptionControlSelected : ""}`}>
          {selected ? <Icon icon="mdi:checkbox-marked" width="18" height="18" /> : <Icon icon="mdi:checkbox-blank-outline" width="18" height="18" />}
        </span>
      );
    }

    return (
      <span className={`${classes.generateOptionControl} ${selected ? classes.generateOptionControlSelected : ""}`}>
        {selected ? <Icon icon="mdi:radiobox-marked" width="18" height="18" /> : <Icon icon="mdi:radiobox-blank" width="18" height="18" />}
      </span>
    );
  };

  const renderDestinationFields = (isDestinationOnly = false) => {
    if (isExamContext) return null;

    return (
      <>
        {isDestinationOnly && (
          <p className={classes.generateDestinationNotice}>
            You can only change the destination and title. Difficulty and question count cannot be edited after generation.
          </p>
        )}

        <div className={classes.generateRadioSection}>
          <p className={classes.generateRadioLabel}>Add questions to</p>
          <div className={classes.generateRadioGroup}>
            {DESTINATIONS.map((destination) => (
              <label key={destination.value} className={classes.generateRadioOption}>
                <input
                  type="radio"
                  name={isDestinationOnly ? "destinationTypeEdit" : "destinationType"}
                  value={destination.value}
                  checked={destinationType === destination.value}
                  onChange={() => handleDestinationTypeChange(destination.value)}
                />
                <span>{destination.label}</span>
              </label>
            ))}
          </div>
        </div>

        {destinationType && (
          <SelectGroup
            label="Title"
            children={
              <select
                value={destinationId}
                onChange={(e) => setDestinationId(e.target.value)}
                disabled={isLoadingDestinations}
              >
                <option value="">
                  {isLoadingDestinations ? "Loading..." : "Select title"}
                </option>
                {destinations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {getDestinationLabel(item)}
                  </option>
                ))}
              </select>
            }
          />
        )}

        {destinationType && !isLoadingDestinations && destinations.length === 0 && (
          <p className={classes.generateEmptyState}>
            No draft or empty {DESTINATIONS.find((d) => d.value === destinationType)?.label?.toLowerCase()}s found.
          </p>
        )}
      </>
    );
  };

  const renderDrawerHeader = () => (
    <div className={classes.generateDrawerHeader}>
      <div>
        <h3 className={classes.generateDrawerTitle}>Generate Questions</h3>
        {isExamContext ? (
          <>
            <p className={classes.generateDrawerContextTitle}>
              {examContext?.title || "Untitled"}
            </p>
            <div className={classes.generateMetaBadges}>
              {examContext?.subjectName ? (
                <span className={classes.generateMetaBadge}>{examContext.subjectName}</span>
              ) : null}
              {examContext?.classLabel ? (
                <span className={classes.generateMetaBadge}>{examContext.classLabel}</span>
              ) : null}
            </div>
          </>
        ) : (
          <p className={classes.generateDrawerSubtitle}>
            {topic ? `From ${topic}` : "From lesson outline"}
          </p>
        )}
      </div>
      <IconButton
        aria-label="close drawer"
        onClick={handleClose}
        size="small"
        className={classes.generateDrawerClose}
      >
        <Icon icon="mdi:close" width="18" height="18" color="#fff" />
      </IconButton>
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        "& .MuiPaper-root": {
          width: 480,
          maxWidth: "100%",
          padding: "24px",
          borderRadius: "16px 0 0 16px",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      {renderDrawerHeader()}

      {step === "setup" ? (
        <>
          <div className={classes.generateDrawerBody}>
            <div className={classes.generateFieldRow}>
              <SelectGroup
                label="Difficulty"
                children={
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                  >
                    {DIFFICULTY_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                }
              />
              <InputGroup
                label="Questions"
                inputType="number"
                inputName="questionCount"
                inputMin={1}
                value={questionCount}
                onChange={(e) => setQuestionCount(e.target.value)}
              />
            </div>

            {isExamContext && (
              <TextAreaGroup
                label="Description"
                inputName="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeHolder="Add context to guide question generation"
              />
            )}

            {renderDestinationFields(false)}
          </div>

          <div className={classes.generateDrawerFooter}>
            <Button
              variant="primaryoutline"
              buttonSize="full"
              color="btnfontprimary"
              buttonName="Cancel"
              onClick={handleClose}
            />
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName="Generate Questions"
              onClick={handleGenerate}
              isLoading={isGenerating}
            />
          </div>
        </>
      ) : (
        <>
          <div className={classes.generateResultsMeta}>
            <p className={classes.generateResultsCount}>
              {generatedQuestions.length} Question{generatedQuestions.length === 1 ? "" : "s"} Generated
            </p>
            {!isExamContext && (
              <button
                type="button"
                className={classes.generateEditSetup}
                onClick={handleEditSetup}
              >
                {showDestinationEditor ? "Hide Setup" : "Edit Setup"}
              </button>
            )}
          </div>

          {!isExamContext && (
            <div
              className={`${classes.generateDestinationEditor} ${showDestinationEditor ? classes.generateDestinationEditorOpen : ""}`}
            >
              <div className={classes.generateDestinationEditorInner}>
                {renderDestinationFields(true)}
              </div>
            </div>
          )}

          <div className={classes.generateDrawerBody}>
            <div className={classes.generateQuestionsList}>
              {generatedQuestions.map((question, index) => (
                <div key={question.localId} className={classes.generateQuestionCard}>
                  <div className={classes.generateQuestionCardHeader}>
                    <p className={classes.generateQuestionText}>
                      {index + 1}. {question.question_text || "Untitled question"}
                    </p>
                    <div className={classes.generateQuestionActions}>
                      <IconButton
                        aria-label="regenerate question"
                        size="small"
                        onClick={() => handleRegenerateQuestion(question.localId)}
                        disabled={regeneratingId === question.localId}
                        className={classes.generateQuestionActionBtn}
                      >
                        <Icon
                          icon={regeneratingId === question.localId ? "eos-icons:loading" : "mdi:refresh"}
                          width="18"
                          height="18"
                          color="#667085"
                        />
                      </IconButton>
                      <IconButton
                        aria-label="delete question"
                        size="small"
                        onClick={() => handleDeleteQuestion(question.localId)}
                        className={classes.generateQuestionActionBtn}
                      >
                        <Icon icon="mdi:trash-can-outline" width="18" height="18" color="#F04438" />
                      </IconButton>
                    </div>
                  </div>

                  <div className={classes.generateOptionsList}>
                    {(question.options || []).map((option, optionIndex) => {
                      const selected = isOptionSelected(question, optionIndex);

                      return (
                        <div
                          key={`${question.localId}-opt-${optionIndex}`}
                          className={`${classes.generateOptionRow} ${selected ? classes.generateOptionRowSelected : ""}`}
                        >
                          {renderOptionControl(question, optionIndex)}
                          <span className={classes.generateOptionText}>{option || `Option ${optionIndex + 1}`}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={classes.generateResultsFooter}>
            <Button
              variant="primary"
              buttonSize="full"
              color="btndefault"
              buttonName={SAVE_LABELS[destinationType] || "Save"}
              onClick={handleSave}
              isLoading={isSaving}
            />
          </div>
        </>
      )}
    </Drawer>
  );
};

GenerateQuestionsDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  topic: PropTypes.string,
  isAdmin: PropTypes.bool,
  lessonOutlineId: PropTypes.string,
  versionId: PropTypes.string,
  initialDestinationType: PropTypes.string,
  initialDestinationId: PropTypes.string,
  examContext: PropTypes.shape({
    title: PropTypes.string,
    subjectName: PropTypes.string,
    subjectId: PropTypes.string,
    classLabel: PropTypes.string,
    description: PropTypes.string
  })
};

export default GenerateQuestionsDrawer;
